import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { ContentIcon } from '@/components/atoms';

import { type DeliveryRoute } from '../hooks/useDeliveryRoutes';

type Props = {
  routes: readonly DeliveryRoute[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  tabId: (index: number) => string;
  panelId: string;
  label: string;
};

/**
 * The destination switch: named places as segmented pills.
 *
 * Built on MUI's `Tabs` rather than a row of buttons of our own. The pills are
 * ours, but arrow-key movement, the roving tabindex and the `aria-selected`
 * bookkeeping are behaviour a tablist has to get exactly right, and
 * re-implementing it here would be three place names' worth of styling bought
 * with a keyboard trap.
 *
 * The indicator is switched off because the selected pill is already filled —
 * two marks for one state reads as two states.
 */
export function RouteTabs({ routes, selectedIndex, onSelect, tabId, panelId, label }: Props) {
  return (
    <Tabs
      value={selectedIndex}
      onChange={(_, next: number) => onSelect(next)}
      aria-label={label}
      variant="scrollable"
      scrollButtons={false}
      slotProps={{ indicator: { sx: { display: 'none' } } }}
      sx={{
        minHeight: 0,
        // The pills are their own surface, so the rail under a default tab row
        // would draw a second, shorter edge across the band.
        '& .MuiTabs-flexContainer': { gap: { xs: 2, sm: 3 } },
        '& .MuiTabs-scroller': { py: 1 },
      }}
    >
      {routes.map((route, index) => (
        <Tab
          key={route.destination}
          id={tabId(index)}
          aria-controls={panelId}
          disableRipple
          label={
            <Stack direction="row" spacing={2} alignItems="center">
              <ContentIcon name={route.icon} sx={{ fontSize: 20 }} />
              <Stack sx={{ textAlign: 'left', minWidth: 0 }}>
                <Typography variant="subtitle2" component="span" sx={{ lineHeight: 1.2 }}>
                  {route.place}
                </Typography>
                {/* No opacity on the country: the tab already sets a colour
                    for each state — `text.secondary` unselected, the brand
                    red's contrast ink when selected — and fading either one
                    takes a compliant pairing below AA for 12px type. The two
                    lines are told apart by size and weight instead. */}
                <Typography variant="caption" component="span" sx={{ lineHeight: 1.3 }}>
                  {route.country}
                </Typography>
              </Stack>
            </Stack>
          }
          sx={{
            minHeight: 0,
            textTransform: 'none',
            alignItems: 'stretch',
            px: { xs: 4, md: 5 },
            py: 3,
            borderRadius: 999,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            color: 'text.secondary',
            transition: 'background-color 200ms ease, border-color 200ms ease, color 200ms ease',
            '&:hover': { borderColor: 'primary.main' },
            '&.Mui-selected': {
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
              borderColor: 'primary.main',
            },
          }}
        />
      ))}
    </Tabs>
  );
}
